/*
 * <crystal-ball> — 3D glass sphere hero.
 *
 * The active product's image is refracted inside a procedural glass sphere
 * rendered with Three.js (vendored at assets/three.module.min.js, r168,
 * loaded on demand via dynamic import so it never downloads unless the
 * section approaches the viewport). Slide state comes from Dawn's
 * slider-component `slideChanged` event; this component adds no scroll
 * handling of its own.
 *
 * Degradation ladder:
 *   no JS      -> static fallback <img>, strip still scroll-snaps natively
 *   no WebGL   -> fallback <img> swapped per slide with a CSS crossfade
 *   WebGL      -> canvas fades in over the fallback image
 */

class CrystalBall extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const dataScript = this.querySelector(`#CrystalBallData-${this.dataset.sectionId}`);
    try {
      this.products = JSON.parse(dataScript.textContent);
    } catch (error) {
      this.products = [];
    }
    if (!this.products.length) return;

    this.stage = this.querySelector('.crystal-ball__stage');
    this.fallbackImg = this.querySelector('img.crystal-ball__fallback');
    this.currentIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.webglActive = false;
    this.rafId = null;
    this.visible = false;
    this.crossfade = null;
    this.spinImpulse = 0;

    this.onSlideChanged = this.onSlideChanged.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.sliderComponent = this.querySelector('slider-component');
    if (this.sliderComponent) {
      this.sliderComponent.addEventListener('slideChanged', this.onSlideChanged);
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          if (entry.isIntersecting && !this.initStarted) {
            this.initStarted = true;
            this.initWebGL();
          }
          this.updateLoopState();
        });
      },
      { rootMargin: '300px' }
    );
    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.teardown();
  }

  /* ---------- slide handling ---------- */

  onSlideChanged(event) {
    const index = Math.min(Math.max((event.detail.currentPage || 1) - 1, 0), this.products.length - 1);
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    if (this.webglActive) {
      this.startCrossfade(index);
    } else {
      this.swapFallbackImage(index);
    }
  }

  swapFallbackImage(index) {
    const product = this.products[index];
    if (!this.fallbackImg || !product.fallback) return;
    const img = this.fallbackImg;
    img.style.opacity = '0';
    const duration = this.reducedMotion ? 0 : 250;
    window.setTimeout(() => {
      img.removeAttribute('srcset');
      img.src = product.fallback;
      img.alt = product.title;
      const reveal = () => {
        img.style.opacity = '';
      };
      if (img.complete) reveal();
      else img.addEventListener('load', reveal, { once: true });
    }, duration);
  }

  /* ---------- WebGL setup ---------- */

  supportsWebGL() {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
    } catch (error) {
      return false;
    }
  }

  async initWebGL() {
    if (!this.supportsWebGL() || !this.dataset.threeUrl) return;

    try {
      const THREE = await import(this.dataset.threeUrl);
      if (!this.isConnected) return;
      this.THREE = THREE;
      this.buildScene();
      await this.loadTextures();
      if (!this.isConnected || !this.renderer) return;
      this.setTexture('uTexA', this.currentIndex);
      this.setTexture('uTexB', this.currentIndex);
      this.webglActive = true;
      this.classList.add('crystal-ball--webgl');
      this.attachSmokeInteractions();
      this.updateLoopState();
      if (this.reducedMotion) this.renderFrame(0);
    } catch (error) {
      // Any failure (import, context loss, texture error) leaves the
      // fully functional 2D fallback in place.
      this.teardownScene();
    }
  }

  buildScene() {
    const THREE = this.THREE;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.stage.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 20);
    this.camera.position.set(0, 0, 3.4);

    const tint = new THREE.Color(this.dataset.tint || '#ffffff');
    const spinSetting = parseFloat(this.dataset.spinSpeed || '3');

    this.uniforms = {
      uTexA: { value: null },
      uTexB: { value: null },
      uMix: { value: 0 },
      uTexAAspect: { value: 1 },
      uTexBAspect: { value: 1 },
      uTexAEnabled: { value: 0 },
      uTexBEnabled: { value: 0 },
      uEnvMap: { value: this.createEnvTexture() },
      uTint: { value: tint },
      uTime: { value: 0 },
      uSpin: { value: 0 },
      uImageScale: { value: Math.min(Math.max(parseFloat(this.dataset.imageScale || '60') / 100, 0.3), 1) },
      uBgKey: { value: this.dataset.removeImageBg === 'false' ? 0 : 1 },
      uCenter: { value: new THREE.Vector3() },
    };
    this.baseSpinSpeed = this.reducedMotion ? 0 : spinSetting * 0.008;

    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      vertexShader: /* glsl */ `
        varying vec3 vWorldNormal;
        varying vec3 vWorldPos;

        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPos.xyz;
          vWorldNormal = normalize(mat3(modelMatrix) * normal);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uTexA;
        uniform sampler2D uTexB;
        uniform float uMix;
        uniform float uTexAAspect;
        uniform float uTexBAspect;
        uniform float uTexAEnabled;
        uniform float uTexBEnabled;
        uniform sampler2D uEnvMap;
        uniform vec3 uTint;
        uniform float uTime;
        uniform float uSpin;
        uniform float uImageScale;
        uniform float uBgKey;
        uniform vec3 uCenter;

        varying vec3 vWorldNormal;
        varying vec3 vWorldPos;

        // Deliberately milder than real glass: a true 1.45 sphere-lens puts
        // the product plane near the focal point and magnifies a few texels
        // across the whole ball. This keeps the image readable while still
        // warping it convincingly.
        const float REFRACT_IOR = 1.0 / 1.12;
        const float GLASS_IOR = 1.0 / 1.45;
        const float PLANE_Z = -1.15;
        const float PLANE_HALF = 1.25;
        const float PI = 3.14159265;

        vec2 equirectUv(vec3 dir) {
          float u = atan(dir.z, dir.x) / (2.0 * PI) + 0.5 + uSpin;
          float v = asin(clamp(dir.y, -1.0, 1.0)) / PI + 0.5;
          return vec2(u, v);
        }

        // Refract the view ray through the front surface and intersect the
        // virtual product plane sitting behind the sphere's centre. All in
        // sphere-local space (uCenter tracks the sphere's world position).
        vec2 refractedPlaneUv(vec3 viewDir, vec3 normal, float ior) {
          vec3 rel = vWorldPos - uCenter;
          vec3 r = refract(viewDir, normal, ior);
          if (abs(r.z) < 1e-4) r.z = -1e-4;
          float t = (PLANE_Z - rel.z) / r.z;
          vec3 hit = rel + r * max(t, 0.0);
          return hit.xy / PLANE_HALF;
        }

        // Cover-fit sample scaled by uImageScale so the image floats inside
        // the glass rather than filling it. Edges fade out softly, corners
        // are rounded off by a circular vignette, and (when uBgKey is on)
        // the white studio backdrop is keyed to transparent — fully at the
        // border, gently in the centre so white labels/products survive.
        vec4 sampleProduct(sampler2D tex, vec2 uv, float aspect, float enabled) {
          vec2 halfExtents = vec2(max(aspect, 1.0), max(1.0 / aspect, 1.0)) * uImageScale;
          vec2 t = uv / halfExtents * 0.5 + 0.5;
          vec2 d = min(t, 1.0 - t);
          float inside = smoothstep(0.0, 0.05, min(d.x, d.y)) * enabled;
          vec4 color = texture2D(tex, clamp(t, 0.0, 1.0));
          float edge = length(t * 2.0 - 1.0);
          float circ = 1.0 - smoothstep(0.78, 1.02, edge);
          // Key only near-pure backdrop white: white garments photograph a
          // touch darker (and with shading), so a tight threshold keeps them
          // solid instead of letting the glass grey them out.
          float whiteness = smoothstep(0.97, 0.997, min(color.r, min(color.g, color.b)));
          float key = whiteness * mix(0.18, 1.0, smoothstep(0.3, 0.9, edge)) * uBgKey;
          return vec4(color.rgb, color.a * inside * circ * (1.0 - key));
        }

        vec4 productAt(vec2 uvR, vec2 uvG, vec2 uvB, sampler2D tex, float aspect, float enabled) {
          float r = sampleProduct(tex, uvR, aspect, enabled).r;
          vec4 g = sampleProduct(tex, uvG, aspect, enabled);
          float b = sampleProduct(tex, uvB, aspect, enabled).b;
          return vec4(r, g.g, b, g.a);
        }

        void main() {
          vec3 normal = normalize(vWorldNormal);
          vec3 viewDir = normalize(vWorldPos - cameraPosition);

          float shimmer = 1.0 + 0.04 * sin(uTime * 1.7 + vWorldPos.y * 4.0);

          // Chromatic aberration: three slightly different refraction ratios.
          vec2 uvR = refractedPlaneUv(viewDir, normal, REFRACT_IOR * 0.99 * shimmer);
          vec2 uvG = refractedPlaneUv(viewDir, normal, REFRACT_IOR * shimmer);
          vec2 uvB = refractedPlaneUv(viewDir, normal, REFRACT_IOR * 1.01 * shimmer);

          vec4 prodA = productAt(uvR, uvG, uvB, uTexA, uTexAAspect, uTexAEnabled);
          vec4 prodB = productAt(uvR, uvG, uvB, uTexB, uTexBAspect, uTexBEnabled);
          vec4 product = mix(prodA, prodB, smoothstep(0.0, 1.0, uMix));

          // Mirror reflection of the environment.
          vec3 reflectDir = reflect(viewDir, normal);
          vec3 reflection = texture2D(uEnvMap, equirectUv(reflectDir)).rgb;

          // Interior glass body: refracted environment lifted toward the
          // reflection so refraction of the dark floor doesn't blotch.
          vec3 refractDir = refract(viewDir, normal, GLASS_IOR);
          vec3 refractedEnv = texture2D(uEnvMap, equirectUv(refractDir)).rgb;
          vec3 glassAmbient = vec3(0.9, 0.92, 0.95);
          vec3 interiorGlass = mix(glassAmbient, mix(refractedEnv, reflection, 0.4), 0.32) * 0.97 * uTint;

          // Soft vignette keeps the product feeling suspended in the glass.
          float vignette = smoothstep(1.35, 0.55, length(uvG));
          vec3 interior = mix(interiorGlass, product.rgb * uTint, product.a * vignette);

          // Schlick fresnel, exaggerated so the environment reads on glass.
          float cosTheta = clamp(dot(-viewDir, normal), 0.0, 1.0);
          float fresnel = 0.08 + 0.92 * pow(1.0 - cosTheta, 4.0);
          fresnel *= shimmer;

          // Constant sheen + fresnel-driven mirror toward the rim. The sheen
          // is kept low so it doesn't grey out the product behind the glass.
          vec3 color = mix(interior, reflection, clamp(0.08 + fresnel * 1.4, 0.0, 1.0));

          // Bright rim at grazing angles.
          color += reflection * pow(1.0 - cosTheta, 6.0) * 0.8;

          float alpha = clamp(0.88 + fresnel * 0.5, 0.0, 1.0);
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });

    this.sphere = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 48), material);

    this.root = new THREE.Group();
    this.root.add(this.sphere);
    if (this.dataset.smoke !== 'false') this.root.add(this.buildSmoke());
    this.showPedestal = this.dataset.pedestal !== 'false';
    if (this.showPedestal) {
      this.root.add(this.buildPedestal());
      // Recenter the taller ball-plus-pedestal composition in frame,
      // biased low to leave headroom for the smoke above the orb.
      this.root.position.y = 0.5;

      // Image-based + key lighting for the pedestal's PBR material, from
      // the sharp (unblurred) environment so reflections have definition.
      const sharpEnv = new THREE.CanvasTexture(this.envSharpCanvas);
      sharpEnv.colorSpace = THREE.SRGBColorSpace;
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.envRT = pmrem.fromEquirectangular(sharpEnv);
      pmrem.dispose();
      sharpEnv.dispose();
      this.envSharpCanvas = null;
      this.scene.environment = this.envRT.texture;
      const keyLight = new THREE.DirectionalLight(0xfff2dd, 1.2);
      keyLight.position.set(2.5, 3, 2);
      this.scene.add(keyLight);
    }
    this.scene.add(this.root);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.stage);
    this.resize();
  }

  // Tap, hover, and scroll all feed a decaying "puff" impulse that speeds
  // the smoke's outward drift and thickens it for a moment.
  puffSmoke(amount, additive) {
    if (!this.webglActive || this.reducedMotion || !this.smokeMaterial) return;
    this.smokePuff = additive ? Math.min(this.smokePuff + amount, 1) : Math.max(this.smokePuff, amount);
  }

  attachSmokeInteractions() {
    if (!this.smokeMaterial) return;
    this.onSmokePointerDown = () => this.puffSmoke(1);
    this.onSmokePointerMove = () => this.puffSmoke(0.05, true);
    this.onSmokeScroll = () => {
      const y = window.scrollY;
      const delta = Math.abs(y - (this.lastScrollY === undefined ? y : this.lastScrollY));
      this.lastScrollY = y;
      if (this.visible) this.puffSmoke(Math.min(delta * 0.004, 0.45), true);
    };
    this.stage.addEventListener('pointerdown', this.onSmokePointerDown);
    this.stage.addEventListener('pointermove', this.onSmokePointerMove);
    window.addEventListener('scroll', this.onSmokeScroll, { passive: true });
  }

  detachSmokeInteractions() {
    if (!this.onSmokeScroll) return;
    this.stage.removeEventListener('pointerdown', this.onSmokePointerDown);
    this.stage.removeEventListener('pointermove', this.onSmokePointerMove);
    window.removeEventListener('scroll', this.onSmokeScroll);
    this.onSmokeScroll = null;
  }

  // Wisps of smoke rising from behind the orb: a single quad running a
  // small FBM-noise shader. One draw call, no particles, no render
  // targets — deliberately cheap so mobile GPUs don't notice it.
  buildSmoke() {
    const THREE = this.THREE;
    const color = new THREE.Color(this.dataset.smokeColor || '#7a3bd6');

    this.smokePuff = 0;
    this.smokePhase = 0;
    const styles = { aura: 0, wisps: 1, fog: 2, vortex: 3 };
    this.smokeMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: this.uniforms.uTime,
        uPhase: { value: 0 },
        uPuff: { value: 0 },
        uStyle: { value: styles[this.dataset.smokeStyle] ?? 0 },
        uSmokeColor: { value: color },
        uOrbCenter: this.uniforms.uCenter,
        // Plane-local distance to the visible canvas edge above/below the
        // smoke's centre; kept in sync with the camera framing in resize().
        uEdgeTop: { value: 1.65 },
        uEdgeBottom: { value: 2.0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vWorldPos;
        void main() {
          vUv = uv;
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPos.xyz;
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform float uPhase;
        uniform float uPuff;
        uniform int uStyle;
        uniform vec3 uSmokeColor;
        uniform vec3 uOrbCenter;
        uniform float uEdgeTop;
        uniform float uEdgeBottom;
        varying vec2 vUv;
        varying vec3 vWorldPos;

        vec2 rot(vec2 v, float a) {
          float c = cos(a);
          float s = sin(a);
          return vec2(c * v.x - s * v.y, s * v.x + c * v.y);
        }

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
            f.y
          );
        }

        float fbm(vec2 p) {
          float v = 0.0;
          float a = 0.5;
          for (int i = 0; i < 4; i++) {
            v += a * noise(p);
            p *= 2.1;
            a *= 0.5;
          }
          return v;
        }

        void main() {
          // uPhase accumulates in JS so interaction "puffs" speed the
          // motion up without phase jumps. uPuff extends reach/opacity.
          vec2 p = (vUv - 0.5) * 5.6;
          float r = length(p);
          float reach = 2.3 + uPuff * 0.7;
          float n = 0.0;
          float density = 0.0;
          vec3 thresholds = vec3(0.2, 0.62, 0.7); // alpha lo / hi / gain

          if (uStyle == 1) {
            // Wisps: incense-like tendrils climbing from behind the orb.
            vec2 q = vec2(p.x * 1.6, p.y * 1.2 - uPhase * 1.4);
            float swirl = fbm(q * 1.5 + uTime * 0.08);
            n = fbm(q + (swirl - 0.5) * 2.2);
            float column = exp(-p.x * p.x * 0.45);
            float base = smoothstep(-1.8, -0.9, p.y);
            float top = 1.0 - smoothstep(0.7, 1.6, p.y + (n - 0.5));
            density = n * column * base * top * 1.6;
            thresholds = vec3(0.28, 0.68, 0.75);
          } else if (uStyle == 2) {
            // Fog: a calm mist pooling low around the pedestal.
            vec2 q = vec2(p.x * 1.1 - uPhase * 0.8, p.y * 1.6);
            float swirl = fbm(q * 1.3 + uTime * 0.05);
            n = fbm(q + (swirl - 0.5) * 1.4);
            float band = (1.0 - smoothstep(-0.7, 0.5, p.y)) * smoothstep(-2.05, -1.5, p.y);
            float wide = exp(-p.x * p.x * 0.18);
            density = n * band * wide;
            thresholds = vec3(0.12, 0.5, 0.55);
          } else if (uStyle == 3) {
            // Vortex: smoke spiralling around the orb (twist grows with
            // radius; rotation keeps the field seamless).
            vec2 q = rot(p, uPhase * 0.5 - r * 0.7) * 1.4;
            float swirl = fbm(q * 1.4 + uTime * 0.07);
            n = fbm(q + (swirl - 0.5) * 1.6);
            float fadeOut = 1.0 - smoothstep(1.3, reach, r + (n - 0.5) * 0.9);
            float fadeIn = smoothstep(0.5, 0.95, r);
            density = n * fadeOut * fadeIn;
            thresholds = vec3(0.25, 0.65, 0.75);
          } else {
            // Aura: radial halo drifting outward from the orb.
            vec2 outward = p / max(r, 0.2);
            vec2 q = p * 1.5 - outward * uPhase;
            float swirl = fbm(q * 1.4 + uTime * 0.07);
            n = fbm(q + (swirl - 0.5) * 1.6);
            float fadeOut = 1.0 - smoothstep(1.3, reach, r + (n - 0.5) * 0.9);
            float fadeIn = smoothstep(0.35, 0.8, r);
            density = n * fadeOut * fadeIn;
          }


          // The glass sphere is intentionally not fully opaque, so anything
          // drawn behind it bleeds through the ball and the product image.
          // Mask smoke inside the orb's silhouette: distance from the
          // camera->fragment ray to the sphere centre. The fade starts just
          // inside the rim (radius 1) — the fresnel makes the glass fully
          // opaque there, so the slight overlap reads as smoke touching the
          // ball rather than a gap around it.
          vec3 rayDir = normalize(vWorldPos - cameraPosition);
          vec3 toOrb = uOrbCenter - cameraPosition;
          float along = dot(toOrb, rayDir);
          float rayDist = sqrt(max(dot(toOrb, toOrb) - along * along, 0.0));
          density *= smoothstep(0.96, 1.07, rayDist);

          float alpha = smoothstep(thresholds.x, thresholds.y, density) * (thresholds.z + uPuff * 0.3);

          // Dissolve against the stage's real top/bottom edges (computed in
          // resize()). Applied to the final alpha — not the density — so the
          // alpha thresholds above can't re-sharpen the fade into a hard line.
          alpha *= 1.0 - smoothstep(uEdgeTop - 0.6, uEdgeTop - 0.05, p.y);
          alpha *= 1.0 - smoothstep(uEdgeBottom - 0.6, uEdgeBottom - 0.05, -p.y);
          vec3 color = mix(uSmokeColor * 0.4, uSmokeColor * 1.05 + vec3(0.03), n);
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });

    const smoke = new THREE.Mesh(new THREE.PlaneGeometry(5.6, 5.6), this.smokeMaterial);
    smoke.position.set(0, 0, -1.4);
    smoke.renderOrder = -1;
    this.smokeMesh = smoke;
    return smoke;
  }

  // Ornate lathe-turned stand the orb hovers over: flared foot, knopped
  // stem, cradle bowl, plus decorative torus bands. One material, all
  // procedural geometry.
  buildPedestal() {
    const THREE = this.THREE;
    const finishes = {
      chrome: { color: 0xf5f7fa, metalness: 1.0, roughness: 0.06, envMapIntensity: 1.6 },
      gold: { color: 0xc9a445, metalness: 1.0, roughness: 0.32, envMapIntensity: 1.2 },
      silver: { color: 0xd6dae0, metalness: 1.0, roughness: 0.22, envMapIntensity: 1.2 },
      obsidian: { color: 0x221c2e, metalness: 0.75, roughness: 0.35, envMapIntensity: 1.2 },
    };
    const style = finishes[this.dataset.pedestalStyle] ? this.dataset.pedestalStyle : 'chrome';
    const finish = finishes[style];
    this.pedestalMaterial = new THREE.MeshStandardMaterial({
      color: finish.color,
      metalness: finish.metalness,
      roughness: finish.roughness,
      envMapIntensity: finish.envMapIntensity,
      side: THREE.DoubleSide,
    });

    // Worn chrome: turning marks, smudges, and scuffs vary the roughness
    // (and micro-bump) so reflections smear and break up realistically.
    if (style === 'chrome') {
      const wear = this.createWearTexture();
      this.pedestalMaterial.roughness = 1.0;
      this.pedestalMaterial.roughnessMap = wear;
      this.pedestalMaterial.bumpMap = wear;
      this.pedestalMaterial.bumpScale = 0.006;
    }

    const pedestal = new THREE.Group();

    // Stepped circular base.
    const baseProfile = [
      [0.0, -2.0], [0.72, -2.0], [0.78, -1.93], [0.6, -1.88], [0.56, -1.83], [0.3, -1.8],
    ].map(([r, y]) => new THREE.Vector2(r, y));
    pedestal.add(new THREE.Mesh(new THREE.LatheGeometry(baseProfile, 64), this.pedestalMaterial));

    // Twisted pentagonal column: rotate each vertex ring by an angle
    // proportional to height so the flat facets spiral. Flat shading keeps
    // the spiral flutes crisp instead of smoothing them away.
    const stemGeometry = new THREE.CylinderGeometry(0.15, 0.3, 0.7, 5, 60);
    const positions = stemGeometry.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);
      const angle = ((y + 0.35) / 0.7) * Math.PI * 2.2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      positions.setXYZ(i, x * cos - z * sin, y, x * sin + z * cos);
    }
    stemGeometry.computeVertexNormals();
    this.stemMaterial = this.pedestalMaterial.clone();
    this.stemMaterial.flatShading = true;
    const stem = new THREE.Mesh(stemGeometry, this.stemMaterial);
    stem.position.y = -1.5;
    pedestal.add(stem);

    // Hub the claws grow from.
    const hubProfile = [
      [0.05, -1.26], [0.3, -1.18], [0.37, -1.08], [0.3, -1.02],
    ].map(([r, y]) => new THREE.Vector2(r, y));
    pedestal.add(new THREE.Mesh(new THREE.LatheGeometry(hubProfile, 48), this.pedestalMaterial));

    // Four talons: tapered curved spikes that hug the orb's surface and
    // hook upward, narrowing to sharp points.
    const talonCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0.28, -1.16, 0),
      new THREE.Vector3(0.58, -1.0, 0),
      new THREE.Vector3(0.72, -0.82, 0),
      new THREE.Vector3(0.82, -0.6, 0),
      new THREE.Vector3(0.87, -0.5, 0),
    ]);
    const talonGeometry = new THREE.TubeGeometry(talonCurve, 28, 0.06, 8);
    const talonPositions = talonGeometry.attributes.position;
    const ringCount = 29; // tubularSegments + 1
    const ringVerts = 9; // radialSegments + 1
    for (let j = 0; j < ringCount; j++) {
      const t = j / (ringCount - 1);
      const center = talonCurve.getPointAt(t);
      const taper = 1 - 0.92 * Math.pow(t, 1.6);
      for (let k = 0; k < ringVerts; k++) {
        const idx = j * ringVerts + k;
        talonPositions.setXYZ(
          idx,
          center.x + (talonPositions.getX(idx) - center.x) * taper,
          center.y + (talonPositions.getY(idx) - center.y) * taper,
          center.z + (talonPositions.getZ(idx) - center.z) * taper
        );
      }
    }
    talonGeometry.computeVertexNormals();
    for (let i = 0; i < 4; i++) {
      const talon = new THREE.Mesh(talonGeometry, this.pedestalMaterial);
      talon.rotation.y = (i * Math.PI) / 2 + Math.PI / 4;
      pedestal.add(talon);
    }

    // Accent rings at the column's ends.
    const band = (radius, tube, y) => {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, tube, 12, 48), this.pedestalMaterial);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = y;
      return ring;
    };
    pedestal.add(band(0.33, 0.045, -1.82));
    pedestal.add(band(0.2, 0.035, -1.17));

    this.pedestal = pedestal;
    return pedestal;
  }

  // Grayscale wear map (absolute roughness): dark = polished mirror,
  // light = worn. Lathe UVs wrap U around the axis, so horizontal lines
  // become turning rings.
  createWearTexture() {
    const THREE = this.THREE;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#2a2c2f';
    ctx.fillRect(0, 0, 512, 512);

    // Fine turning/brush rings.
    for (let y = 0; y < 512; y += 2 + Math.random() * 4) {
      ctx.fillStyle = `rgba(255, 255, 255, ${0.02 + Math.random() * 0.06})`;
      ctx.fillRect(0, y, 512, 1);
    }

    // Smudges and dull patches.
    for (let i = 0; i < 42; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const r = 18 + Math.random() * 70;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(190, 190, 190, ${0.05 + Math.random() * 0.16})`);
      g.addColorStop(1, 'rgba(190, 190, 190, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }

    // Scuff strokes.
    for (let i = 0; i < 30; i++) {
      ctx.strokeStyle = `rgba(220, 220, 220, ${0.08 + Math.random() * 0.18})`;
      ctx.lineWidth = 1 + Math.random() * 1.5;
      ctx.beginPath();
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      ctx.moveTo(x, y);
      ctx.lineTo(x + (Math.random() - 0.5) * 90, y + (Math.random() - 0.5) * 24);
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 1);
    return texture;
  }

  // Procedural 1024x512 equirect "studio": vertical gradient plus softbox
  // stripes. Cheap stand-in for an HDRI; swap for a real equirect JPEG by
  // pointing this texture at an image instead.
  createEnvTexture() {
    const THREE = this.THREE;
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
    sky.addColorStop(0, '#f4f6fa');
    sky.addColorStop(0.42, '#aab4c4');
    sky.addColorStop(0.55, '#565f70');
    sky.addColorStop(1, '#3a4150');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const softbox = (x, y, radius, brightness, tintColor) => {
      const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
      grad.addColorStop(0, `rgba(255, 255, 255, ${brightness})`);
      grad.addColorStop(0.35, `${tintColor}`);
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    };
    softbox(150, 110, 150, 1, 'rgba(255, 250, 240, 0.75)');
    softbox(520, 140, 100, 0.9, 'rgba(240, 245, 255, 0.6)');
    softbox(850, 90, 170, 1, 'rgba(255, 252, 245, 0.8)');
    softbox(330, 350, 70, 0.35, 'rgba(200, 215, 255, 0.2)');
    softbox(710, 370, 80, 0.3, 'rgba(255, 235, 215, 0.18)');

    // Elongated soft light blooms (studio key lights). Gradient falloff —
    // hard-edged shapes reflect as literal rectangles on the glass.
    const bloom = (x, y, rx, ry, alpha, rotation) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.scale(rx, ry);
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
      g.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
      g.addColorStop(0.55, `rgba(255, 255, 255, ${alpha * 0.3})`);
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(-1, -1, 2, 2);
      ctx.restore();
    };
    bloom(170, 80, 150, 38, 0.85, -0.08);
    bloom(870, 70, 165, 42, 0.8, 0.1);
    bloom(530, 120, 95, 26, 0.5, 0.05);

    // Keep a sharp copy for the pedestal's PMREM environment — a mirror
    // finish needs crisp highlights to reflect or it reads as flat.
    this.envSharpCanvas = document.createElement('canvas');
    this.envSharpCanvas.width = canvas.width;
    this.envSharpCanvas.height = canvas.height;
    this.envSharpCanvas.getContext('2d').drawImage(canvas, 0, 0);

    // Soften via downscale/upscale, and sample without mipmaps: mipmapped
    // equirect lookups produce a visible seam arc inside the orb where the
    // atan() longitude wraps and the UV derivative explodes.
    const soft = document.createElement('canvas');
    soft.width = 256;
    soft.height = 128;
    const softCtx = soft.getContext('2d');
    softCtx.drawImage(canvas, 0, 0, soft.width, soft.height);
    ctx.drawImage(soft, 0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  }

  loadTextures() {
    const THREE = this.THREE;
    const loader = new THREE.TextureLoader();
    const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();

    this.textures = new Array(this.products.length).fill(null);
    const loads = this.products.map(
      (product, index) =>
        new Promise((resolve) => {
          if (!product.image) return resolve();
          loader.load(
            product.image,
            (texture) => {
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.anisotropy = Math.min(4, maxAnisotropy);
              this.textures[index] = texture;
              resolve();
            },
            undefined,
            () => resolve()
          );
        })
    );
    return Promise.all(loads);
  }

  setTexture(slot, index) {
    const texture = this.textures[index];
    const product = this.products[index];
    this.uniforms[slot].value = texture;
    this.uniforms[`${slot}Aspect`].value = product.aspect || 1;
    this.uniforms[`${slot}Enabled`].value = texture ? 1 : 0;
  }

  startCrossfade(index) {
    // If a fade is mid-flight, promote its target first so we fade from
    // whatever is currently most visible.
    if (this.crossfade) this.promoteTexB();
    this.setTexture('uTexB', index);
    this.crossfade = {
      start: performance.now(),
      duration: this.reducedMotion ? 120 : 600,
    };
    this.spinImpulse = this.reducedMotion ? 0 : 0.03;
    this.updateLoopState();
  }

  promoteTexB() {
    this.uniforms.uTexA.value = this.uniforms.uTexB.value;
    this.uniforms.uTexAAspect.value = this.uniforms.uTexBAspect.value;
    this.uniforms.uTexAEnabled.value = this.uniforms.uTexBEnabled.value;
    this.uniforms.uMix.value = 0;
    this.crossfade = null;
  }

  /* ---------- render loop ---------- */

  updateLoopState() {
    const animating = this.crossfade !== null || !this.reducedMotion;
    const shouldRun =
      this.webglActive && this.visible && document.visibilityState === 'visible' && animating;

    if (shouldRun && this.rafId === null) {
      this.lastFrame = performance.now();
      this.rafId = requestAnimationFrame((now) => this.tick(now));
    } else if (!shouldRun && this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  tick(now) {
    this.rafId = null;
    this.renderFrame(now);
    this.updateLoopState();
  }

  renderFrame(now) {
    const delta = Math.min((now - (this.lastFrame || now)) / 1000, 0.1);
    this.lastFrame = now;

    this.uniforms.uTime.value += delta;
    this.spinImpulse *= Math.pow(0.02, delta); // decays to ~nothing in 1s
    if (this.spinImpulse < 1e-4) this.spinImpulse = 0;
    this.uniforms.uSpin.value += (this.baseSpinSpeed + this.spinImpulse) * delta * 10.0;

    if (this.crossfade) {
      const progress = Math.min((now - this.crossfade.start) / this.crossfade.duration, 1);
      const eased =
        progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      this.uniforms.uMix.value = eased;
      if (progress >= 1) this.promoteTexB();
    }

    // Gentle idle bob (hovers over the pedestal cradle).
    this.sphere.position.y = this.reducedMotion ? 0 : Math.sin(this.uniforms.uTime.value * 0.6) * 0.03;
    this.uniforms.uCenter.value.set(0, this.root.position.y + this.sphere.position.y, 0);

    if (this.smokeMaterial) {
      this.smokePuff *= Math.pow(0.12, delta);
      if (this.smokePuff < 1e-3) this.smokePuff = 0;
      this.smokePhase += (0.45 + this.smokePuff * 1.8) * delta;
      this.smokeMaterial.uniforms.uPhase.value = this.smokePhase;
      this.smokeMaterial.uniforms.uPuff.value = this.smokePuff;
    }

    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    if (!this.renderer) return;
    const width = this.stage.clientWidth;
    const height = this.stage.clientHeight;
    if (!width || !height) return;
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;

    // Frame the composition (taller when the pedestal is shown) with
    // breathing room on any aspect ratio.
    const fovTan = Math.tan((this.camera.fov * Math.PI) / 360);
    const halfHeight = this.showPedestal ? 1.85 : 1.15;
    const halfWidth = 1.25;
    this.camera.position.z = Math.max(halfHeight, halfWidth / Math.max(this.camera.aspect, 0.3)) / fovTan;
    this.camera.updateProjectionMatrix();

    // Tell the smoke shader where the visible canvas actually ends, in the
    // smoke plane's local units (the plane sits 1.4 behind the root, which
    // is lifted by root.position.y when the pedestal shows). Clamped inside
    // the quad's 2.8 half-size so the fade completes before the geometry edge.
    if (this.smokeMaterial) {
      const halfVisible = (this.camera.position.z + 1.4) * fovTan;
      this.smokeMaterial.uniforms.uEdgeTop.value = Math.min(halfVisible - this.root.position.y, 2.7);
      this.smokeMaterial.uniforms.uEdgeBottom.value = Math.min(halfVisible + this.root.position.y, 2.7);
    }
  }

  onVisibilityChange() {
    this.updateLoopState();
  }

  /* ---------- teardown ---------- */

  teardownScene() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.sphere) {
      this.sphere.geometry.dispose();
      this.sphere.material.dispose();
      this.sphere = null;
    }
    if (this.pedestal) {
      this.pedestal.traverse((child) => child.geometry && child.geometry.dispose());
      if (this.pedestalMaterial.roughnessMap) this.pedestalMaterial.roughnessMap.dispose();
      this.pedestalMaterial.dispose();
      if (this.stemMaterial) {
        this.stemMaterial.dispose();
        this.stemMaterial = null;
      }
      this.pedestal = null;
      this.pedestalMaterial = null;
    }
    this.detachSmokeInteractions();
    if (this.smokeMesh) {
      this.smokeMesh.geometry.dispose();
      this.smokeMaterial.dispose();
      this.smokeMesh = null;
      this.smokeMaterial = null;
    }
    if (this.envRT) {
      this.envRT.dispose();
      this.envRT = null;
    }
    if (this.uniforms) {
      if (this.uniforms.uEnvMap.value) this.uniforms.uEnvMap.value.dispose();
      this.uniforms = null;
    }
    if (this.textures) {
      this.textures.forEach((texture) => texture && texture.dispose());
      this.textures = null;
    }
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
      this.renderer = null;
    }
    this.webglActive = false;
    this.classList.remove('crystal-ball--webgl');
  }

  teardown() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.sliderComponent) {
      this.sliderComponent.removeEventListener('slideChanged', this.onSlideChanged);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.teardownScene();
    this.initialized = false;
    this.initStarted = false;
  }
}

customElements.define('crystal-ball', CrystalBall);
